import { useEffect, useRef, useState } from "react";
import * as Tone from "tone";

export interface UseTransportResult {
  playing: boolean;
  bpm: number;
  toggle: () => Promise<void>;
  setBpm: (value: number) => void;
  randomiseBpm: () => void;
  restartTransport: () => void;
}

function randomBpm() {
  return Math.floor(Math.random() * 21) + 70; // 70–90
}

// On iOS, Web Audio uses the "ambient" session category by default, which
// respects the hardware silent/ringer switch. Playing a real HTMLMediaElement
// on the first user gesture causes iOS to upgrade the tab's session to
// "playback", which ignores the switch. This only needs to run once.
let iosAudioSessionUnlocked = false;

function unlockIOSAudioSession(): void {
  const sampleRate = 8000;
  const n = sampleRate; // 1 second of silence
  const bytes = new Uint8Array(44 + n);
  const view = new DataView(bytes.buffer);
  const write = (offset: number, text: string) =>
    [...text].forEach((c, i) => { bytes[offset + i] = c.charCodeAt(0); });
  write(0, "RIFF"); view.setUint32(4, 36 + n, true); write(8, "WAVE");
  write(12, "fmt "); view.setUint32(16, 16, true); view.setUint16(20, 1, true); view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true); view.setUint32(28, sampleRate, true);
  view.setUint16(32, 1, true); view.setUint16(34, 8, true);
  write(36, "data"); view.setUint32(40, n, true);
  bytes.fill(128, 44); // 0x80 = silence for unsigned 8-bit PCM
  const url = URL.createObjectURL(new Blob([bytes.buffer], { type: "audio/wav" }));
  const audio = new Audio(url);
  audio.play().catch(() => {});
  audio.addEventListener("ended", () => URL.revokeObjectURL(url), { once: true });
}

export function useTransport(): UseTransportResult {
  const [playing, setPlaying] = useState(false);
  const bpm = useRef(randomBpm());

  useEffect(() => {
    Tone.Transport.bpm.value = bpm.current;
    Tone.Transport.swing = 0.5;
    Tone.Transport.swingSubdivision = "8n";

    return () => {
      Tone.Transport.stop();
      Tone.Transport.cancel();
    };
  }, []);

  async function toggle() {
    if (Tone.context.state !== "running") {
      // Fire before awaiting anything — iOS user gesture window is synchronous.
      if (!iosAudioSessionUnlocked) {
        iosAudioSessionUnlocked = true;
        unlockIOSAudioSession();
      }
      await Tone.start();
    }
    if (Tone.Transport.state === "started") {
      Tone.Transport.stop();
      setPlaying(false);
    } else {
      Tone.Transport.start();
      setPlaying(true);
    }
  }

  function setBpm(value: number) {
    bpm.current = value;
    Tone.Transport.bpm.value = value;
  }

  function randomiseBpm() {
    setBpm(randomBpm());
  }

  function restartTransport() {
    Tone.Transport.stop();
    Tone.Transport.position = "0:0:0";
    Tone.Transport.start();
  }

  return { playing, bpm: bpm.current, toggle, setBpm, randomiseBpm, restartTransport };
}
